import { useEffect, useRef, useState } from "react";
import "../incident.css";
import {
  DESCRIPTION_MAX, IIncident, INCIDENT_TYPES, IOrderIncidents, IncidentType, RISK_META, typeIcon, typeLabel,
} from "../models/IIncident";
import { createIncident, getOrderIncidents, incidentError, newIdempotencyKey, uploadEvidence } from "../services/incidentService";
import { StatusBadge, formatDateTime, formatDay } from "./IncidentBadges";

interface Props {
  orderId: string;
  orderCode?: string;
  customerName?: string;
  /** "manager": restoran yöneticisi/personeli — müşteri geçmişi ve bu siparişteki bildirimler de gösterilir.
   *  "courier": atanmış kurye — geçmiş gösterilmez (API kuryeye 403 döner). */
  mode: "manager" | "courier";
  /** "Hazırlık başladıktan sonra iptal" türü yalnız iptal edilmiş siparişe açılabilir */
  orderCancelled?: boolean;
  onClose: () => void;
  onCreated?: (incident: IIncident) => void;
}

const MAX_IMAGE = 5 * 1024 * 1024;

export default function ReportIncidentModal({ orderId, orderCode, customerName, mode, orderCancelled, onClose, onCreated }: Props) {
  const [type, setType] = useState<IncidentType | null>(null);
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ incident: IIncident; evidenceError?: string } | null>(null);

  const [info, setInfo] = useState<IOrderIncidents | null>(null);
  const [infoLoading, setInfoLoading] = useState(mode === "manager");

  // Aynı form için tekrar denemede aynı anahtar gider; böylece ağ kopması çift kayıt açmaz.
  const idemKey = useRef(newIdempotencyKey());

  useEffect(() => {
    if (mode !== "manager") return;
    getOrderIncidents(orderId)
      .then((res) => setInfo(res.data))
      .catch(() => setInfo(null))
      .finally(() => setInfoLoading(false));
  }, [orderId, mode]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !saving) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  // Tür ya da açıklama değişirse bu artık farklı bir istek — yeni anahtar.
  useEffect(() => { idemKey.current = newIdempotencyKey(); }, [type, description]);

  const alreadyReported = new Set((info?.ownIncidents ?? []).filter((i) => i.status !== "Removed" && i.status !== "Rejected").map((i) => i.type));
  const trimmed = description.trim();
  const canSubmit = !!type && trimmed.length > 0 && trimmed.length <= DESCRIPTION_MAX && !saving;

  const pickFile = (f: File | null) => {
    if (f && f.size > MAX_IMAGE) { setError("Görsel en fazla 5 MB olabilir."); return; }
    setError(null);
    setFile(f);
  };

  const submit = async () => {
    if (!canSubmit || !type) return;
    setSaving(true);
    setError(null);
    try {
      const res = await createIncident(orderId, type, trimmed, idemKey.current);
      let evidenceError: string | undefined;
      if (file) {
        try { await uploadEvidence(res.data.id, file); }
        catch (e) { evidenceError = incidentError(e, "Görsel yüklenemedi; bildirim yine de kaydedildi."); }
      }
      setDone({ incident: res.data, evidenceError });
      onCreated?.(res.data);
    } catch (e) {
      setError(incidentError(e, "Bildirim gönderilemedi. Tekrar deneyin."));
    } finally {
      setSaving(false);
    }
  };

  const risk = info?.customerRisk;
  const riskMeta = risk ? RISK_META[risk.level] : null;

  return (
    <div className="inc-backdrop" onClick={() => !saving && onClose()}>
      <div className="inc-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="inc-modal__head">
          <div className="inc-modal__icon" style={{ background: "#fef2f2", color: "#dc2626" }}>
            <i className="bi bi-flag-fill"></i>
          </div>
          <div style={{ minWidth: 0 }}>
            <div className="inc-modal__title">Müşteri sorunu bildir</div>
            <div className="inc-modal__sub">
              {orderCode ? `#${orderCode}` : "Sipariş"}{customerName ? ` · ${customerName}` : ""}
            </div>
          </div>
          <button className="inc-close" onClick={onClose} disabled={saving} aria-label="Kapat"><i className="bi bi-x-lg"></i></button>
        </div>

        {done ? (
          <>
            <div className="inc-modal__body">
              <div className="inc-alert inc-alert--success">
                <i className="bi bi-check-circle-fill"></i>
                <div>
                  <strong>Bildirim kaydedildi.</strong>
                  <div style={{ marginTop: 2 }}>
                    {done.incident.status === "PendingConfirmation"
                      ? "Restoran yöneticisinin onayından sonra müşteri geçmişine yansıyacak."
                      : "Bildirim onaylı olarak müşteri geçmişine eklendi."}
                  </div>
                </div>
              </div>
              {done.evidenceError && (
                <div className="inc-alert inc-alert--warn"><i className="bi bi-image"></i><div>{done.evidenceError}</div></div>
              )}
              <div className="inc-item">
                <div className="inc-item__top">
                  <i className={`bi ${typeIcon(done.incident.type)}`} style={{ color: "#dc2626" }}></i>
                  <span className="inc-item__title">{typeLabel(done.incident.type)}</span>
                  <StatusBadge status={done.incident.status} />
                </div>
                <div className="inc-item__desc">{done.incident.description}</div>
              </div>
            </div>
            <div className="inc-modal__foot">
              <button className="inc-btn inc-btn--dark" onClick={onClose}>Tamam</button>
            </div>
          </>
        ) : (
          <>
            <div className="inc-modal__body">
              {mode === "manager" && (
                infoLoading ? (
                  <div className="inc-hint"><span className="spinner-border spinner-border-sm me-2" role="status" />Müşteri geçmişi kontrol ediliyor…</div>
                ) : (
                  <>
                    {risk && riskMeta && risk.level !== "None" && (
                      <div className="inc-risk" style={{ background: riskMeta.bg, color: riskMeta.color }}>
                        <i className={`bi ${riskMeta.icon}`} style={{ fontSize: "1.1rem" }}></i>
                        <div style={{ fontSize: ".8rem" }}>
                          <strong>{riskMeta.label}:</strong> Bu müşteri için son 3 ayda {risk.distinctOrderCount} ayrı siparişte olay bildirildi.
                          {risk.incidents && risk.incidents.length > 0 && (
                            <div className="inc-risk__chips">
                              {risk.incidents.map((x, i) => (
                                <span key={i} className="inc-badge" style={{ background: "rgba(255,255,255,.7)", color: riskMeta.color }}>
                                  {typeLabel(x.type)} · {formatDay(x.date)}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {info && info.ownIncidents.length > 0 && (
                      <div>
                        <div className="inc-label">Bu siparişte açılmış bildirimler</div>
                        <div className="inc-list">
                          {info.ownIncidents.map((i) => (
                            <div key={i.id} className="inc-item">
                              <div className="inc-item__top">
                                <span className="inc-item__title" style={{ fontSize: ".8rem" }}>{typeLabel(i.type)}</span>
                                <StatusBadge status={i.status} />
                              </div>
                              <div className="inc-item__meta"><span>{formatDateTime(i.createdAtUtc)}</span></div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )
              )}

              <div>
                <div className="inc-label">Ne oldu?</div>
                <div className="inc-types">
                  {INCIDENT_TYPES.map((t) => {
                    const disabled = (t.onlyCancelled && !orderCancelled) || alreadyReported.has(t.value);
                    return (
                      <button
                        key={t.value}
                        type="button"
                        className={`inc-type${type === t.value ? " inc-type--active" : ""}`}
                        disabled={disabled}
                        title={alreadyReported.has(t.value) ? "Bu siparişe bu türde bildirim zaten var" : t.onlyCancelled && !orderCancelled ? "Yalnız iptal edilmiş siparişlerde" : undefined}
                        onClick={() => setType(t.value)}
                      >
                        <i className={`bi ${t.icon}`}></i>{t.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <div className="inc-label">
                  <span>Açıklama</span>
                  <span style={{ color: trimmed.length > DESCRIPTION_MAX ? "#dc2626" : "#9ca3af", fontWeight: 600 }}>
                    {description.length}/{DESCRIPTION_MAX}
                  </span>
                </div>
                <textarea
                  className="inc-input"
                  rows={4}
                  maxLength={DESCRIPTION_MAX}
                  placeholder="Ne yaşandığını kısaca yazın (ör. 3 kez zil çaldım, telefonu açmadı, 10 dk bekledim)."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
                <div className="inc-hint">Kişisel veri yazmayın; diğer restoranlar yalnız tür ve tarihi görür, açıklamayı görmez.</div>
              </div>

              <div>
                <div className="inc-label"><span>Kanıt görseli <span style={{ color: "#9ca3af", fontWeight: 600 }}>(isteğe bağlı)</span></span></div>
                {file ? (
                  <div className="inc-item" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <i className="bi bi-image" style={{ color: "#dc2626" }}></i>
                    <span style={{ fontSize: ".8rem", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{file.name}</span>
                    <button className="inc-btn inc-btn--sm" onClick={() => setFile(null)} disabled={saving}>Kaldır</button>
                  </div>
                ) : (
                  <label className="inc-btn inc-btn--sm" style={{ width: "fit-content" }}>
                    <i className="bi bi-camera"></i> Fotoğraf ekle
                    <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" hidden onChange={(e) => pickFile(e.target.files?.[0] ?? null)} />
                  </label>
                )}
                <div className="inc-hint">jpg, png, gif veya webp · en fazla 5 MB</div>
              </div>

              {mode === "courier" && (
                <div className="inc-alert inc-alert--info">
                  <i className="bi bi-info-circle-fill"></i>
                  <div>Bildiriminiz restoran yöneticisi onayladıktan sonra müşteri geçmişine yansır.</div>
                </div>
              )}

              {error && <div className="inc-alert inc-alert--error"><i className="bi bi-exclamation-circle-fill"></i><div>{error}</div></div>}
            </div>

            <div className="inc-modal__foot">
              <button className="inc-btn" onClick={onClose} disabled={saving}>Vazgeç</button>
              <button className="inc-btn inc-btn--danger" onClick={submit} disabled={!canSubmit}>
                {saving ? <span className="spinner-border spinner-border-sm" role="status" /> : <><i className="bi bi-send-fill"></i> Bildir</>}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
